import { useEffect, useRef } from "react";
import type { InvestigationLine } from "@omnabase/shared";
import { AiTag, Button, InvestigationLines } from "@omnabase/ui";

/** What a running code investigation is doing (2026-10-02): the card stands where the investigation's note will appear (under the
 *  thread, above a suggested draft) and grows a line per search, read and scout as the worker records them; at most 400 px high,
 *  then it scrolls and follows the newest line. When the note lands the ticket stops investigating and the card gives way to the
 *  note, which keeps the steps behind "Show the steps". Team only, like the note. */
export function InvestigationCard({ lines }: { lines: InvestigationLine[] }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { const el = box.current; if (el) el.scrollTop = el.scrollHeight; }, [lines.length]);
  return (
    <div className="ob-draft-preview ob-draft-preview--generating ob-investigation" role="status" aria-label="What the investigation is doing">
      <div className="ob-draft-preview__head"><AiTag generating>Investigating the code</AiTag><span className="ob-draft-preview__note">Team only · becomes a note when the AI is done</span></div>
      <div className="ob-draft-preview__body ob-investigation__scroll" ref={box}>
        {lines.length === 0 && <div className="ob-draft-preview__label">Waiting for the first step. What the AI searches and reads appears here.</div>}
        <InvestigationLines lines={lines} />
        <div className="ob-draft-preview__lines" aria-hidden="true"><span /></div>
      </div>
    </div>
  );
}

/** An investigation that could not run (the composer-and-fixes round, 2026-11-03; brief rule 13, the red team's finding 2): the card stays where
 *  the note would have appeared, in the AI's tint, with the run's recorded words, "Try again" (a new run) and "Dismiss" (for the whole team).
 *  Shown from the ticket read's `investigationFailed` while no run is going; a stop by a person is not a failure. Team only. */
export function InvestigationFailedCard({ error, onRetry, onDismiss }: { error: string; onRetry: () => void; onDismiss: () => void }) {
  return (
    <div className="ob-draft-preview ob-investigation ob-investigation--failed" role="status" aria-label="The investigation failed">
      <div className="ob-draft-preview__head">
        <AiTag>The investigation failed</AiTag>
        {/* outside the tools, which a phone hides: the failure can be retried and put away anywhere */}
        <span className="ob-draft-preview__discard"><Button variant="ghost" size="sm" icon="refresh" className="ob-btn--ai" onClick={onRetry}>Try again</Button><Button variant="ghost" size="sm" onClick={onDismiss}>Dismiss</Button></span>
      </div>
      <div className="ob-draft-preview__body"><div className="ob-draft-preview__label">Could not investigate: {error}</div></div>
    </div>
  );
}
